import type { StatusPresentation } from '@fleet/shared';
import type { BookingStatus } from '@/api/types';

/**
 * How a booking's state looks and reads.
 *
 * Colour, a word and a shape — never colour alone, so the board still works for
 * a colourblind staff member and in direct sunlight on a phone.
 */
const BOOKING_STATUS_MAP: Readonly<Record<BookingStatus, StatusPresentation>> = {
  enquiry: { label: 'Enquiry', tone: 'neutral', shape: 'ring' },
  quoted: { label: 'Quoted', tone: 'neutral', shape: 'dot' },
  confirmed: { label: 'Confirmed', tone: 'active', shape: 'square' },
  on_trip: { label: 'On trip', tone: 'active', shape: 'bar' },
  returned: { label: 'Returned', tone: 'attention', shape: 'dot' },
  closed: { label: 'Closed', tone: 'done', shape: 'square' },
  cancelled: { label: 'Cancelled', tone: 'alert', shape: 'cross' },
  no_show: { label: 'No show', tone: 'alert', shape: 'cross' },
};

/** Every status has an entry, so this never falls through — but say so in code. */
export function bookingStatus(value: BookingStatus | undefined): StatusPresentation {
  return BOOKING_STATUS_MAP[value ?? 'confirmed'] ?? BOOKING_STATUS_MAP.confirmed;
}

const ENQUIRY_STATUS_MAP: Readonly<Record<string, StatusPresentation>> = {
  new: { label: 'New', tone: 'attention', shape: 'ring' },
  quoted: { label: 'Quoted', tone: 'neutral', shape: 'dot' },
  won: { label: 'Booked', tone: 'active', shape: 'square' },
  lost: { label: 'Lost', tone: 'alert', shape: 'cross' },
};

export function enquiryStatus(value: string | undefined): StatusPresentation {
  return ENQUIRY_STATUS_MAP[value ?? 'new'] ?? ENQUIRY_STATUS_MAP['new']!;
}

export const ENQUIRY_SOURCES = [
  { value: 'phone', label: 'Phone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'viber', label: 'Viber' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'website', label: 'Website form' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
] as const;

export const BOOKING_SOURCES = [
  { value: 'phone', label: 'Phone call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'viber', label: 'Viber' },
  { value: 'walk_in', label: 'Walk-in' },
  { value: 'website', label: 'Website enquiry' },
  { value: 'repeat', label: 'Repeat customer' },
  { value: 'agent', label: 'Agent or hotel' },
] as const;

export const FUEL_POLICIES = [
  { value: 'same_to_same', label: 'Return with the same fuel' },
  { value: 'full_to_full', label: 'Full to full' },
  { value: 'prepaid', label: 'Fuel prepaid' },
] as const;

/** Verbs, so a button says what it does rather than what the state is. */
export const TRANSITION_VERBS: Readonly<Record<string, string>> = {
  quoted: 'Send quote',
  confirmed: 'Confirm booking',
  on_trip: 'Mark as gone out',
  returned: 'Check it back in',
  closed: 'Close booking',
  cancelled: 'Cancel booking',
  no_show: 'Mark a no-show',
};
